import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Mail, KeyRound, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreceLogo } from "@/components/CreceLogo";

export default function StaffLogin({ role, redirectTo, title, subtitle, accent = "emerald" }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/staff-login", { email, password, role });
      login(data.token, data.user);
      toast.success(`${title} login successful`);
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const grad = accent === "amber"
    ? "radial-gradient(circle at 30% 20%, #92400E 0%, transparent 50%), radial-gradient(circle at 70% 80%, #064E3B 0%, transparent 50%)"
    : "radial-gradient(circle at 30% 20%, #064E3B 0%, transparent 50%), radial-gradient(circle at 70% 80%, #D97706 0%, transparent 50%)";

  return (
    <div className="min-h-screen grid place-items-center bg-stone-900 p-6 relative overflow-hidden" data-testid={`${role}-login-page`}>
      <div className="absolute inset-0 opacity-20" style={{ background: grad }} />
      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center mb-8">
          <CreceLogo size="lg" theme="light" />
          <div className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-400">
            <Shield className="w-3.5 h-3.5" /> {title} Console
          </div>
        </div>
        <div className="bg-stone-50 rounded-2xl p-8 shadow-2xl">
          <h1 className="font-display text-3xl font-black tracking-tight text-stone-900">{title} Sign in</h1>
          <p className="text-sm text-stone-500 mt-1">{subtitle}</p>
          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <Label className="text-stone-700 font-semibold text-sm">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                <Input data-testid={`${role}-email-input`} type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required autoComplete="username"
                  placeholder={`${role}@crece.in`}
                  className="pl-10 h-12 rounded-lg border-stone-300 focus:border-emerald-900 focus:ring-emerald-900/20" />
              </div>
            </div>
            <div>
              <Label className="text-stone-700 font-semibold text-sm">Password</Label>
              <div className="relative mt-1.5">
                <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                <Input data-testid={`${role}-password-input`} type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                  className="pl-10 h-12 rounded-lg border-stone-300 focus:border-emerald-900 focus:ring-emerald-900/20" />
              </div>
            </div>
            <Button type="submit" data-testid={`${role}-login-submit`} disabled={loading}
              className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
              {loading ? "Signing in..." : "Sign In"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>
          <div className="mt-6 pt-5 border-t border-stone-200 text-center">
            <Link to="/" className="text-xs text-stone-500 hover:text-emerald-900">← Back to public site</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
